import axios, { AxiosResponse } from 'axios';
import { useCallback, useEffect, useState } from 'react';
import qs from 'query-string';
import url from 'url';

import { DataReq, DataReqItem, OptionType } from '../types';

import { useQuery } from 'hooks';

const trailingSlash = (str: string) => (str.endsWith('/') ? str : str + '/');

export const useApi = ({
  onSearch,
  apiUrl,
  propsValue,
  defaultValue,
  name,
  renderOption,
  loadOnOpen,
  loadOnEveryOpen,
  loadOption,
  onLoaded,
}: // eslint-disable-next-line @typescript-eslint/no-explicit-any
any) => {
  // TODO: Remove any
  const [isLoading, setIsLoading] = useState(false);
  const [loadedOptions, setLoadedOptions] = useState<OptionType[]>([]);

  const { query, replaceQuery } = useQuery();

  const fetchData = useCallback(
    async (search?: string) => {
      let fetchedData: OptionType[] = [];

      if (!apiUrl) {
        !onSearch && console.error('apiUrl is undefined');
        return;
      }

      try {
        setIsLoading(true);
        const { data }: AxiosResponse<DataReq<'id'>> = await axios.get(apiUrl, {
          params: { search },
        });

        fetchedData =
          data.results?.map(option => ({
            ...option,
            label: renderOption ? renderOption(option) : option.name,
            value: String(option.id),
          })) || [];

        // option is not additionally loaded by id
        if (query[name] && !loadOption) {
          if (Array.isArray(query[name])) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const matchOptions = (query[name] as Array<string>).reduce((acc: any, current) => {
              fetchedData.find(opt => String(opt.value) === current) && acc.push(current);

              return acc;
            }, []);

            matchOptions.length !== query[name]?.length && replaceQuery({ [name]: matchOptions });
          } else {
            !fetchedData.find(opt => String(opt.value) === query[name]) &&
              replaceQuery({ [name]: undefined });
          }
        }

        // load option by selected value if not in fetched list
        const idToLoad = propsValue || defaultValue || query[name];
        if (
          loadOption &&
          idToLoad &&
          !fetchedData.find(item => String(item.value) === String(idToLoad))
        ) {
          const parsedUrl = qs.parseUrl(apiUrl);
          const itemUrl = url.format({
            pathname: `${trailingSlash(parsedUrl.url)}${idToLoad}`,
            query: parsedUrl.query,
          });
          const { data }: AxiosResponse<DataReqItem<'id'>> = await axios.get(itemUrl);

          fetchedData.push({
            ...data,
            label: renderOption ? renderOption(data) : data.name,
            value: String(data.id),
          });
        }

        setLoadedOptions(fetchedData);
      } catch (error) {
        console.error('Error from Select: ', error);
      } finally {
        setIsLoading(false);
        onLoaded && onLoaded();
      }

      return fetchedData; // TODO: eslint fixed
    },
    [
      apiUrl,
      onSearch,
      query,
      name,
      loadOption,
      propsValue,
      defaultValue,
      renderOption,
      replaceQuery,
      onLoaded,
    ],
  );

  const onOpenHandle = useCallback(
    (open: boolean) => {
      if (open && apiUrl && ((loadOnOpen && !loadedOptions.length) || loadOnEveryOpen)) {
        fetchData();
      }
    },
    [apiUrl, loadOnOpen, loadedOptions.length, loadOnEveryOpen, fetchData],
  );

  const onSearchHandle = useCallback(
    (search: string) => {
      fetchData(search);
    },
    [fetchData],
  );

  const onSelectHandle = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (searchValue: any) => {
      if (apiUrl && searchValue) fetchData();
    },
    [apiUrl, fetchData],
  );

  // Init fetch data
  useEffect(() => {
    if (loadOnOpen) return;
    if (apiUrl) fetchData();
  }, [apiUrl, fetchData, loadOnOpen]);

  return { isLoading, loadedOptions, onOpenHandle, onSearchHandle, onSelectHandle };
};
